import { useState } from 'react';

const PartnerAdmin = ({ onAddPartner, partners = [] }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    category: 'technology',
    description: '',
    established: new Date().getFullYear().toString()
  });

  const categories = ['technology', 'research', 'aerospace', 'robotics'];

  const handleSubmit = (e) => {
    e.preventDefault();
    const newPartner = {
      ...formData,
      id: Date.now(), // Simple ID generation
    };
    onAddPartner(newPartner);
    setFormData({
      name: '',
      logo: '',
      website: '',
      category: 'technology',
      description: '',
      established: new Date().getFullYear().toString()
    });
    setIsFormOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="bg-slate-900 p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-white">Partner Management</h3>
        <button
          onClick={() => setIsFormOpen(!isFormOpen)}
          className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-lg hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-300 shadow-lg"
        >
          {isFormOpen ? 'Cancel' : 'Add Partner'}
        </button>
      </div>

      {isFormOpen && (
        <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 bg-slate-800 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Partner Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                placeholder="Enter partner name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Website URL *
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Logo URL
              </label>
              <input
                type="url"
                name="logo"
                value={formData.logo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Established Year
              </label>
              <input
                type="number"
                name="established"
                value={formData.established}
                onChange={handleInputChange}
                min="1900"
                max={new Date().getFullYear()}
                className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
              placeholder="Brief description of the partner..."
            />
          </div>

          <button
            type="submit"
            className="w-full px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-lg hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-300 shadow-lg"
          >
            Add Partner
          </button>
        </form>
      )}

      {/* Partners List */}
      <div className="space-y-3">
        <h4 className="text-lg font-semibold text-white">Current Partners ({partners.length})</h4>
        {partners.length === 0 ? (
          <p className="text-gray-400">No partners added yet.</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {partners.map((partner) => (
              <div key={partner.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  {partner.logo && (
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className="w-8 h-8 object-cover rounded"
                    />
                  )}
                  <div>
                    <p className="text-white font-medium">{partner.name}</p>
                    <p className="text-gray-400 text-sm">{partner.category}</p>
                  </div>
                </div>
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-md hover:bg-white/20 hover:border-white/30 transition-all duration-300 text-sm"
                >
                  Visit
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerAdmin;